/**
 * Accessible typewriter effect with reduced motion support
 */

import { prefersReducedMotion } from '../core/utils.js';

export class Typewriter {
  #element;
  #phrases;
  #currentPhrase = 0;
  #currentChar = 0;
  #isDeleting = false;
  #isPaused = false;
  #typeSpeed = 50;
  #deleteSpeed = 25;
  #pauseDuration = 3000;
  #timeout = null;

  constructor(elementId, phrases, options = {}) {
    this.#element = document.getElementById(elementId);
    if (!this.#element) {
      console.warn(`[Typewriter] Element #${elementId} not found`);
      return;
    }

    this.#phrases = phrases;
    this.#typeSpeed = options.typeSpeed || 50;
    this.#deleteSpeed = options.deleteSpeed || 25;
    this.#pauseDuration = options.pauseDuration || 3000;

    // Disable for reduced motion
    if (prefersReducedMotion()) {
      this.#element.textContent = phrases[0];
      return;
    }

    this.#start();
  }

  #start() {
    this.#tick();
  }

  #tick = () => {
    if (this.#isPaused || !this.#element) return;

    const currentPhrase = this.#phrases[this.#currentPhrase];

    if (this.#isDeleting) {
      this.#currentChar--;
      this.#element.textContent = currentPhrase.substring(0, this.#currentChar);
    } else {
      this.#currentChar++;
      this.#element.textContent = currentPhrase.substring(0, this.#currentChar);
    }

    let nextTick = this.#isDeleting ? this.#deleteSpeed : this.#typeSpeed;

    // Add variance to typing speed
    if (!this.#isDeleting) {
      nextTick += Math.random() * 30;
    }

    // Check completion states
    if (!this.#isDeleting && this.#currentChar === currentPhrase.length) {
      // Finished typing - pause then delete
      this.#isPaused = true;
      this.#timeout = setTimeout(() => {
        this.#isPaused = false;
        this.#isDeleting = true;
        this.#tick();
      }, this.#pauseDuration);
      return;
    }

    if (this.#isDeleting && this.#currentChar === 0) {
      // Finished deleting - move to next phrase
      this.#isDeleting = false;
      this.#currentPhrase = (this.#currentPhrase + 1) % this.#phrases.length;
      nextTick = 500; // Pause before typing next
    }

    this.#timeout = setTimeout(this.#tick, nextTick);
  };

  stop() {
    this.#isPaused = true;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
  }

  resume() {
    this.#isPaused = false;
    this.#tick();
  }

  destroy() {
    this.stop();
  }
}
